import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { FREE_CO_OWNER_QUOTA, IAccessLevel, IPrototypePerson } from './prototypeData';
import { RelationshipSelectField } from '../../components/RelationshipSelectField';
import { IRelationship } from '../../caremap/ICareMap';
import { HeaderPopover } from '../../components/HeaderPopover';
import { CareMapState } from '../../caremap/CareMapState';

// ─────────────────────────────────────────────────────────────────────────────
// IH-6912 spike — Add Person flow:
//   Steps — sequential, no defaults, every decision its own step
//
// Sharing & Access owns the data, co-owner shown up-front with
// "X of 2 free used" quota, no silent permission assumptions.
// Submit is a no-op in the spike — onAdd() bubbles a new pending row up.
// ─────────────────────────────────────────────────────────────────────────────

const useStyles = createUseStyles({
    switcherBar: {
        display: 'flex',
        gap: 4,
        padding: 3,
        background: 'var(--ion-color-step-50)',
        borderRadius: 8,
        border: '1px dashed #e0a800'
    },
    switcherLabel: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#7a5b00',
        padding: '6px 8px',
        alignSelf: 'center'
    },
    switcherBtn: {
        flex: 1,
        background: 'transparent',
        border: 0,
        padding: '6px 8px',
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ion-color-medium)',
        borderRadius: 6,
        cursor: 'pointer'
    },
    switcherBtnOn: {
        background: '#fff',
        color: 'var(--ion-text-color)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
    },

    flowHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        justifyContent: 'space-between',
        marginTop: 8
    },
    backBtn: {
        background: 'none',
        border: 0,
        color: 'var(--ion-color-primary)',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        padding: 0
    },
    stepLabel: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--ion-color-primary)'
    },
    flowTitle: {
        fontSize: 19,
        fontWeight: 700,
        color: 'var(--ion-text-color)',
        lineHeight: 1.25,
        marginTop: 8
    },
    flowSub: {
        fontSize: 13,
        color: 'var(--ion-color-medium)',
        marginTop: 4,
        lineHeight: 1.45
    },
    // Small "(i)" affordance sitting at the end of the care-map description,
    // mirroring the access-info button over in SharingAndAccessSheet.
    infoBtn: {
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: 'var(--ion-color-step-100)',
        color: 'var(--ion-color-step-750)',
        border: 0,
        fontSize: 11,
        fontWeight: 700,
        fontStyle: 'italic',
        fontFamily: 'Georgia, serif',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        verticalAlign: 'middle',
        marginLeft: 6
    },
    careMapPopoverBody: {
        fontSize: 13,
        color: 'var(--ion-color-step-750)',
        lineHeight: 1.5
    },
    careMapPopoverImg: {
        display: 'block',
        width: '100%',
        maxWidth: 240,
        height: 'auto',
        margin: '0 auto 12px',
        borderRadius: 8
    },

    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        marginTop: 12
    },
    label: {
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ion-color-step-750)'
    },
    input: {
        border: '1px solid var(--ion-color-step-150)',
        borderRadius: 8,
        padding: '10px 12px',
        fontSize: 14,
        background: '#fff',
        outline: 'none',
        fontFamily: 'inherit'
    },

    accessGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        marginTop: 8
    },
    accessCard: {
        border: '1px solid var(--ion-color-step-150)',
        borderRadius: 10,
        padding: '12px 12px 14px',
        background: '#fff',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 5
    },
    accessCardOn: {
        borderColor: 'var(--ion-color-primary)',
        background: 'rgba(17,120,157,0.06)',
        boxShadow: '0 0 0 1px var(--ion-color-primary)'
    },
    accessCardDisabled: {
        opacity: 0.6,
        cursor: 'not-allowed'
    },
    accessName: {
        fontSize: 14,
        fontWeight: 700,
        color: 'var(--ion-text-color)'
    },
    accessDesc: {
        fontSize: 12,
        color: 'var(--ion-color-medium)',
        lineHeight: 1.4
    },
    quotaBadge: {
        display: 'inline-block',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        padding: '2px 6px',
        borderRadius: 4,
        background: 'rgba(146,61,172,0.12)',
        color: 'var(--ion-color-tertiary)',
        alignSelf: 'flex-start'
    },
    quotaBadgePaid: {
        background: 'rgba(238,108,78,0.12)',
        color: 'var(--ion-color-secondary)'
    },

    yesNoRow: {
        display: 'flex',
        gap: 10,
        marginTop: 8
    },
    yesNoBtn: {
        flex: 1,
        border: '1px solid var(--ion-color-step-150)',
        borderRadius: 8,
        padding: '12px',
        fontSize: 14,
        fontWeight: 600,
        background: '#fff',
        color: 'var(--ion-color-step-750)',
        cursor: 'pointer'
    },
    yesNoBtnOn: {
        borderColor: 'var(--ion-color-primary)',
        background: 'rgba(17,120,157,0.06)',
        color: 'var(--ion-color-primary)'
    },

    review: {
        marginTop: 16,
        padding: 14,
        background: 'var(--ion-color-step-50)',
        borderRadius: 10
    },
    reviewLine: {
        fontSize: 13,
        color: 'var(--ion-text-color)',
        lineHeight: 1.5
    },
    reviewLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--ion-color-medium)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginTop: 8
    },

    footer: {
        display: 'flex',
        gap: 10,
        marginTop: 18,
        // Stick to the bottom of the scrolling sheet so action buttons never
        // scroll out of view when the form is taller than the viewport.
        position: 'sticky',
        bottom: 0,
        background: '#fff',
        paddingTop: 12,
        paddingBottom: 4,
        borderTop: '1px solid var(--ion-color-step-100)',
        zIndex: 1
    },
    cancelBtn: {
        flex: 1,
        background: '#fff',
        border: '1px solid var(--ion-color-primary)',
        color: 'var(--ion-color-primary)',
        borderRadius: 8,
        padding: '12px',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer'
    },
    primaryBtn: {
        flex: 2,
        background: 'var(--ion-color-primary)',
        border: 0,
        color: '#fff',
        borderRadius: 8,
        padding: '12px',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer'
    },
    primaryBtnDisabled: {
        opacity: 0.4,
        cursor: 'not-allowed'
    }
});

export interface IAddPersonFlowProps {
    existingCoOwnerCount: number;
    onCancel: () => void;
    onAdd: (person: Omit<IPrototypePerson, 'id' | 'status'>) => void;
    patientFirstName: string;
}

export const AddPersonFlow: FC<IAddPersonFlowProps> = (props) => {
    return <StepsVariant {...props} />;
};

// Shared bits — kept as local helpers so the flow stays a single readable
// function body.

// Picks the same age-keyed care map illustration the onboarding step uses
// (see OnboardingModel's "Visualize ... Care" step), so the concept looks
// identical in both places.
const useOnboardingCareMapImage = (): string => {
    const careMapState = useInstance(CareMapState);
    const dob = careMapState.getActiveCaremap()?.patient?.dob;

    if (dob) {
        const ageMs = Date.now() - new Date(dob).getTime();
        const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);
        if (ageYears <= 25) {
            return '/assets/images/onboarding/caremap-eleanor.png';
        }
        if (ageYears <= 50) {
            return '/assets/images/onboarding/caremap-samantha.png';
        }
    }

    return '/assets/images/onboarding/caremap-arthur.png';
};

interface IAccessCardProps {
    level: IAccessLevel;
    selected: boolean;
    existingCoOwnerCount: number;
    onSelect: () => void;
}

const AccessCard: FC<IAccessCardProps> = ({ level, selected, existingCoOwnerCount, onSelect }) => {
    const classes = useStyles();
    const isCoOwner = level === 'co-owner';
    const overFreeQuota = isCoOwner && existingCoOwnerCount >= FREE_CO_OWNER_QUOTA;

    return (
        <div
            className={`${classes.accessCard} ${selected ? classes.accessCardOn : ''}`}
            onClick={onSelect}
        >
            <div className={classes.accessName}>{isCoOwner ? 'Co-Owner' : 'Viewer'}</div>
            <div className={classes.accessDesc}>
                {isCoOwner
                    ? 'Helps manage notes, recordings, appointments, and tasks alongside you. Sees everything, including hidden info.'
                    : 'Sees shared notes, recordings, and appointments. Can comment but can\'t edit or see hidden info.'}
            </div>
            {isCoOwner && (
                <span className={`${classes.quotaBadge} ${overFreeQuota ? classes.quotaBadgePaid : ''}`}>
                    {overFreeQuota
                        ? `${existingCoOwnerCount} of ${FREE_CO_OWNER_QUOTA} free used · paid`
                        : `${existingCoOwnerCount} of ${FREE_CO_OWNER_QUOTA} free used`}
                </span>
            )}
        </div>
    );
};

const isValidEmail = (s: string): boolean => /\S+@\S+\.\S+/.test(s);

// ────────────────────────────────────────────────────────────────────────────
// Steps — sequential, no defaults, every decision its own step.
// ────────────────────────────────────────────────────────────────────────────

type IStepNum = 1 | 2 | 3 | 4 | 5;

const StepsVariant: FC<IAddPersonFlowProps> = ({ existingCoOwnerCount, onCancel, onAdd, patientFirstName }) => {
    const classes = useStyles();
    const [step, setStep] = useState<IStepNum>(1);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [access, setAccess] = useState<IAccessLevel | undefined>(undefined);
    const [saveInfo, setSaveInfo] = useState<boolean | undefined>(undefined);
    // Relationship picker writes both fields; label is what we display, id is the canonical key.
    const [relationshipLabel, setRelationshipLabel] = useState<string | undefined>(undefined);
    const [relationshipId, setRelationshipId] = useState<IRelationship | undefined>(undefined);

    const step1Valid = firstName.trim() && lastName.trim() && isValidEmail(email);
    const step2Valid = !!access;
    const step3Valid = saveInfo !== undefined;
    const step4Valid = !!relationshipLabel;

    // Step 4 (relationship) only appears when the user chose to save info. If not,
    // Next from Step 3 skips straight to Review (Step 5), and Back from 5 returns
    // to 3. Keeps each step about one decision instead of conditional fields appearing.
    const nextStepFrom = (current: IStepNum): IStepNum => {
        if (current === 3 && saveInfo === false) {
            return 5;
        }
        return Math.min(current + 1, 5) as IStepNum;
    };
    const prevStepFrom = (current: IStepNum): IStepNum => {
        if (current === 5 && saveInfo === false) {
            return 3;
        }
        return Math.max(current - 1, 1) as IStepNum;
    };

    const back = () => {
        if (step === 1) {
            onCancel();
        } else {
            setStep(prevStepFrom(step));
        }
    };

    const next = () => setStep(nextStepFrom(step));

    const submit = () => {
        if (!access || saveInfo === undefined) {
            return;
        }
        onAdd({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            access,
            onCareMap: saveInfo,
            careMapRole: saveInfo && relationshipLabel ? relationshipLabel : undefined
        });
    };

    // Pronouns kept separate so we don't accidentally render things like
    // "Save z info" (missing 's) or glue 'them' onto a possessive.
    const name = firstName.trim();
    const possessive = name ? `${name}'s` : 'their';

    const [showCareMapInfo, setShowCareMapInfo] = useState(false);
    const careMapImage = useOnboardingCareMapImage();

    return (
        <>
            <div className={classes.flowHeader}>
                <button className={classes.backBtn} onClick={back}>‹ Back</button>
                <div className={classes.stepLabel}>Step {step} of 5</div>
            </div>

            {step === 1 && (
                <>
                    <div className={classes.flowTitle}>Who are you inviting?</div>
                    <div className={classes.flowSub}>We'll send their invite by email.</div>
                    <div className={classes.field}>
                        <span className={classes.label}>First name</span>
                        <input
                            className={classes.input}
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className={classes.field}>
                        <span className={classes.label}>Last name</span>
                        <input
                            className={classes.input}
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                        />
                    </div>
                    <div className={classes.field}>
                        <span className={classes.label}>Email</span>
                        <input
                            className={classes.input}
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </div>
                    <div className={classes.footer}>
                        <button className={classes.cancelBtn} onClick={onCancel}>Cancel</button>
                        <button
                            className={`${classes.primaryBtn} ${!step1Valid ? classes.primaryBtnDisabled : ''}`}
                            onClick={next}
                            disabled={!step1Valid}
                        >
                            Next
                        </button>
                    </div>
                </>
            )}

            {step === 2 && (
                <>
                    <div className={classes.flowTitle}>What can {firstName.trim() || 'they'} see and do?</div>
                    <div className={classes.flowSub}>You can change this later.</div>
                    <div className={classes.accessGrid}>
                        <AccessCard
                            level="viewer"
                            selected={access === 'viewer'}
                            existingCoOwnerCount={existingCoOwnerCount}
                            onSelect={() => setAccess('viewer')}
                        />
                        <AccessCard
                            level="co-owner"
                            selected={access === 'co-owner'}
                            existingCoOwnerCount={existingCoOwnerCount}
                            onSelect={() => setAccess('co-owner')}
                        />
                    </div>
                    <div className={classes.footer}>
                        <button className={classes.cancelBtn} onClick={back}>Back</button>
                        <button
                            className={`${classes.primaryBtn} ${!step2Valid ? classes.primaryBtnDisabled : ''}`}
                            onClick={next}
                            disabled={!step2Valid}
                        >
                            Next
                        </button>
                    </div>
                </>
            )}

            {step === 3 && (
                <>
                    <div className={classes.flowTitle}>Also save {possessive} contact info?</div>
                    <div className={classes.flowSub}>
                        Adds {name || 'them'} to {patientFirstName}'s care map, so everyone helping out can see who else is involved, with contact info a tap away.
                        <button
                            id="care-map-info-trigger"
                            className={classes.infoBtn}
                            onClick={() => setShowCareMapInfo(v => !v)}
                            aria-label="What's a care map?"
                        >
                            i
                        </button>
                    </div>
                    <div className={classes.yesNoRow}>
                        <button
                            className={`${classes.yesNoBtn} ${saveInfo === true ? classes.yesNoBtnOn : ''}`}
                            onClick={() => setSaveInfo(true)}
                        >
                            Yes, Add Them
                        </button>
                        <button
                            className={`${classes.yesNoBtn} ${saveInfo === false ? classes.yesNoBtnOn : ''}`}
                            onClick={() => setSaveInfo(false)}
                        >
                            Not Now
                        </button>
                    </div>
                    <div className={classes.footer}>
                        <button className={classes.cancelBtn} onClick={back}>Back</button>
                        <button
                            className={`${classes.primaryBtn} ${!step3Valid ? classes.primaryBtnDisabled : ''}`}
                            onClick={next}
                            disabled={!step3Valid}
                        >
                            Next
                        </button>
                    </div>
                </>
            )}

            {step === 4 && (
                <>
                    <div className={classes.flowTitle}>Relationship to {patientFirstName}</div>
                    <div className={classes.flowSub}>
                        How is {firstName.trim() || 'this person'} related to {patientFirstName}?
                    </div>
                    <div className={classes.field}>
                        <RelationshipSelectField
                            label={`Relationship to ${patientFirstName}`}
                            required
                            relationshipId={relationshipId}
                            relationship={relationshipLabel}
                            onChange={({ relationshipId: id, relationship: label }) => {
                                setRelationshipId(id);
                                setRelationshipLabel(label);
                            }}
                        />
                    </div>
                    <div className={classes.footer}>
                        <button className={classes.cancelBtn} onClick={back}>Back</button>
                        <button
                            className={`${classes.primaryBtn} ${!step4Valid ? classes.primaryBtnDisabled : ''}`}
                            onClick={next}
                            disabled={!step4Valid}
                        >
                            Next
                        </button>
                    </div>
                </>
            )}

            {step === 5 && (
                <>
                    <div className={classes.flowTitle}>Ready to Send?</div>
                    <div className={classes.flowSub}>
                        We'll email {name || 'them'} an invite.
                    </div>
                    <div className={classes.review}>
                        <div className={classes.reviewLabel}>Person</div>
                        <div className={classes.reviewLine}>{firstName} {lastName} · {email}</div>
                        <div className={classes.reviewLabel}>Access</div>
                        <div className={classes.reviewLine}>
                            {access === 'co-owner' ? 'Co-Owner — full access' : 'Viewer — can see shared notes & comment'}
                        </div>
                        <div className={classes.reviewLabel}>Care map</div>
                        <div className={classes.reviewLine}>
                            {saveInfo && relationshipLabel
                                ? `${relationshipLabel} of ${patientFirstName}`
                                : 'Not added to care map'}
                        </div>
                    </div>
                    <div className={classes.footer}>
                        <button className={classes.cancelBtn} onClick={back}>Back</button>
                        <button className={classes.primaryBtn} onClick={submit}>
                            Send Invite
                        </button>
                    </div>
                </>
            )}

            {/* Reuses the onboarding Care Map explanation + illustration so the
                concept reads the same here as it does during onboarding. */}
            <HeaderPopover
                trigger="care-map-info-trigger"
                isOpen={showCareMapInfo}
                onDidDismiss={() => setShowCareMapInfo(false)}
                title="What's a Care Map?"
                content={
                    <div className={classes.careMapPopoverBody}>
                        <img className={classes.careMapPopoverImg} src={careMapImage} alt="Care map" />
                        A Care Map is a visual overview of all the people and places involved in {patientFirstName}'s care. In Innerhive, it also serves as an intuitive place to organize and store care team details, including essential contact info — keeping everything easily accessible.
                    </div>
                }
            />
        </>
    );
};
